import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const getSessionId = (): string => {
  let id = sessionStorage.getItem('ftb_session');
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('ftb_session', id);
  }
  return id;
};

const send = (data: object) => {
  try {
    fetch('/api/track/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }).catch(() => {});
  } catch {}
};

const sendBeaconFallback = (data: object) => {
  try {
    navigator.sendBeacon(
      '/api/track/',
      new Blob([JSON.stringify(data)], { type: 'application/json' })
    );
  } catch {
    send(data);
  }
};

// Exported utility — call from any component to track custom events
export const trackEvent = (
  eventType: string,
  label = '',
  durationSeconds?: number
) => {
  send({
    event_type: eventType,
    path: window.location.pathname,
    label,
    duration_seconds: durationSeconds ?? null,
    session_id: getSessionId(),
  });
};

const usePageTracking = () => {
  const location = useLocation();
  const startTime = useRef(Date.now());
  const currentPath = useRef(location.pathname);

  useEffect(() => {
    const path = location.pathname;
    const prev = currentPath.current;

    // Send duration for the page we're leaving
    if (prev !== path) {
      const duration = Math.round((Date.now() - startTime.current) / 1000);
      send({
        event_type: 'pageleave',
        path: prev,
        duration_seconds: duration,
        session_id: getSessionId(),
      });
    }

    // Track the new pageview
    send({
      event_type: 'pageview',
      path,
      session_id: getSessionId(),
    });

    startTime.current = Date.now();
    currentPath.current = path;
  }, [location.pathname]);

  // Send duration when tab is hidden or closed
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const duration = Math.round((Date.now() - startTime.current) / 1000);
        sendBeaconFallback({
          event_type: 'pageleave',
          path: currentPath.current,
          duration_seconds: duration,
          session_id: getSessionId(),
        });
      } else {
        // Tab became visible again — reset timer
        startTime.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);
};

export default usePageTracking;
